/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import { IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import GitHubIcon from "@mui/icons-material/GitHub";
import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/loginbackground.jpg";
import { setLoggedIn, setToken, setUser, useMaterialUIController, setLicenseMessage, setRefreshToken } from "../../../context";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import brandWhite from "assets/images/login_page_logo.png";
import mastercardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import pciLogo from "assets/images/pcilogo.png";
import botimLogo from "assets/images/botim.png";
import version from "../../../version";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const {isLoggedIn} = controller;
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = yup.object().shape({
    username: yup
      .string()
      .required("Username is required")
      .max(15, "Username cannot exceed 15 characters"),
    password: yup
      .string()
      .required("Password is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = (data) => {
    api.post('auth/admin/authenticate', {
      username: data.username,
      passwd: data.password
    })
      .then(response => {
        if(response.data.token) {


          let newUser = response.data.user;
          if(newUser.authStatus === "7"){
            let passwdExpiry = new Date(newUser.newUsrPasswdExp);
            const currentDate = new Date();
            // console.log(passwdExpiry, currentDate, passwdExpiry < currentDate);
            if (passwdExpiry < currentDate) {
              toast.error("Your password has been already expired. Please contact the admin.")
            } else {
              const timeDifference = passwdExpiry - currentDate;

              const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
              const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
              // console.log("Welcome "+newUser.firstName+` , Please reset your password, your default password will be expired in ${days} days, ${hours} hours, ${minutes} minutes`)

              toast.success("Welcome "+newUser.firstName+` , Please reset your password to Proceed`);
                            setLoggedIn(dispatch, true);
              setUser(dispatch, response.data.user);
              setToken(dispatch, response.data.token);
              setRefreshToken(dispatch, response.data.refreshToken);
              setLicenseMessage(dispatch, response.data.license);
              navigate("/first-password");
           
            }
          } else if(newUser.authStatus === "1"){
            toast.success("Welcome "+response.data.user.firstName);
            setLoggedIn(dispatch, true);
            setUser(dispatch, response.data.user);
            setToken(dispatch, response.data.token);
            setRefreshToken(dispatch, response.data.refreshToken);
            setLicenseMessage(dispatch, response.data.license);
            navigate("/dashboard");
          }

        }
      })
      .catch(error => {
        const response = error.response;
        if(response) toast.error(response.data.message);
        if (response.data.message == "Your Password has been Expired. Please change it."){
          console.log("no password change required.")
          navigate("/forgot-password", {
            state: {
              username: data.username
            }
          });
        } else {
          console.log("no password change required.")
        }
      });
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  return (
    <BasicLayout image={bgImage} hideFooter>
      <MDBox display="flex" alignItems="center" justifyContent="center" mb={4}>
        <MDBox component="img" src={brandWhite} alt="Youcloud" sx={{ height: "128px" }} />
      </MDBox>
      <Card sx={{ minHeight: "370px", display: "flex", flexDirection: "column" }}>
        <MDBox pt={4} pb={1} px={3} textAlign="left">
          <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ letterSpacing: "0.5px" }}>
            {t('welcome')}
          </MDTypography>
        </MDBox>
        <MDBox
          pt={1}
          pb={3}
          px={3}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <MDBox
            component="form"
            role="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
          >
            <MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Username"
                  fullWidth
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  inputProps={{ maxLength: 50 }}
                  {...register("username")}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register("password")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          style={{
                            background: "transparent",
                            borderRadius: "0",
                            boxShadow: "none",
                            padding: 0,
                            marginRight: "10px",
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                {/* Remember Me Section - Made more compact */}
                <MDBox display="flex" alignItems="center" sx={{ maxWidth: '45%' }}>
                  <Switch
                    checked={rememberMe}
                    name="remember_me"
                    onChange={handleSetRememberMe}
                    size="small" // Made switch smaller
                  />
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    onClick={handleSetRememberMe}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: "0.75rem" // Reduced font size
                    }}
                  >
                    {t('remember_me')}
                  </MDTypography>
                </MDBox>

                {/* Forgot Password Section - Made more compact */}
                <MDBox
                  component="a"
                  href="/forgot-password"
                >
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: "0.75rem" // Reduced font size
                    }}
                  >
                    {t('forgot_password')}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            <MDBox mb={1}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth>
                {t('sign_in')}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={3} mt={3}>
        <MDBox component="img" src={mastercardLogo} alt="Mastercard" sx={{ height: "28px" }} />
        <MDBox component="img" src={visaLogo} alt="Visa" sx={{ height: "28px" }} />
        <MDBox component="img" src={pciLogo} alt="PCI DSS Compliant" sx={{ height: "28px" }} />
        <MDBox component="img" src={botimLogo} alt="Botim" sx={{ height: "28px" }} />
      </MDBox>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mt={1.5} sx={{ whiteSpace: "nowrap" }}>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          &copy; {new Date().getFullYear()} YouCloud Technologies. All Rights Reserved.
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          {t('version')} {version}
        </MDTypography>
      </MDBox>
    </BasicLayout>
  );
}

export default Basic;
